import { Form, Formik } from 'formik';
import get from 'lodash/get';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useToggle } from 'react-use';
import useApi from '../../_hooks/useApi';
import { PrimaryButton, SecondaryButton } from '../../CommonComponents/Button';
import ConfirmationModal from '../../CommonComponents/ConfirmationModal/ConfirmationModalView';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import {
  currentClientSelector,
  isAdviserArea as isAdviserAreaSelector,
  selectIsHiddenLeversRTQ,
  selectShowPortfolioProjectedReturns,
  showRiskToleranceWorkflowSelector,
} from '../../CommonSelectors/CommonAreaSelector';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { showOPLConfirmationDialog } from '../../helpers/showOPLConfirmationDialog';
import { ProfilePageHeader } from '../../ProfileOPL/components/ProfilePageHeader/ProfilePageHeader';
import { RiskProfileSummary } from '../../RiskProfileSummary/RiskProfileSummary';
import { WorkflowIdEnum } from '../../Workflow/api/useWorkflowDataQuery';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { HiddenLeversRiskScore } from '../Components/workflows/HiddenLeversRTQ/components/HiddenLeversRiskScore/HiddenLeversRiskScore';
import LoadingView from './components/LoadingView';
import { OrionResult } from './components/OrionResult/OrionResult';
import { PortfolioPicker } from './components/PortfolioPicker';
import { setSelectedGroupModel } from './RiskToleranceActions';
import {
  ADVISER_AREA_SUBTITLE,
  ASSIGN_RISKALYZE_PORTFOLIO,
  CONSUMER_AREA_SUBTITLE,
  FETCH_RISK_TOLERANCE,
} from './RiskToleranceConstants';
import {
  selectDisclosures,
  selectGroupOptions,
  selectGroupsById,
  selectIsRiskSourceOrion,
  selectRiskalyseProfile,
  selectSelectedGroupId,
  selectSelectedModelId,
  selectShowRiskalysePopup,
} from './RiskToleranceSelector';
import { Container, Disclosure, Footer, WorkflowWrapper } from './RiskToleranceStyles';
import { GroupOptionsDTO } from './RiskTolerance.types';

interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  showPortfolioSelection?: boolean;
}

const RiskTolerance: React.FC<Props> = ({
  clientId,
  workflowProps,
  showPortfolioSelection = true,
}) => {
  const dispatch = useDispatch();

  const isAdviserArea = useSelector(isAdviserAreaSelector);
  const showRiskToleranceWorkflow = useSelector(showRiskToleranceWorkflowSelector);
  const groupOptions = useSelector(selectGroupOptions) as GroupOptionsDTO[];
  const groupsById = useSelector(selectGroupsById);
  const disclosure = useSelector(selectDisclosures);
  const selectedGroupId = useSelector(selectSelectedGroupId);
  const selectedModelId =
    useSelector(selectSelectedModelId) ||
    get(groupsById, `${selectedGroupId}.defaultPortfolioId`, null);
  const riskalyseProfile = useSelector(selectRiskalyseProfile);
  const currentClient = useSelector(currentClientSelector);
  const showRiskalysePopup = useSelector(selectShowRiskalysePopup);
  const isRiskSourceFromOrion = useSelector(selectIsRiskSourceOrion);
  const isHiddenLeversRTQ = useSelector(selectIsHiddenLeversRTQ);
  const showPortfolioProjectedReturns = useSelector(selectShowPortfolioProjectedReturns);

  const [dataChanged, toggleDataChanged] = useToggle(false);

  const [fetchRiskTolerance, { loading: loadingRiskTolerance }] = useApi(
    urlWithParams('/Consumer/RiskToleranceStep2', {
      clientId,
      withRiskalyse: currentClient.HasOrionRiskAlyseIntegration,
    }),
    {
      reduxActionPrefix: FETCH_RISK_TOLERANCE,
    },
  );

  const [saveRiskTolerance] = useApi('/Consumer/RiskToleranceUpdate2/Save', {
    method: 'POST',
    bodyEncoding: 'formData',
  });

  const [assignRiskalyzePortfolio, { loading: loadingAssignRiskalyzePortfolio }] = useApi(
    urlWithParams('/Consumer/RiskToleranceUpdate2/PromptAssignRiskalysePortfolio', {
      clientId,
      assignRiskalysePortfolio: 'true',
    }),
    { method: 'POST', reduxActionPrefix: ASSIGN_RISKALYZE_PORTFOLIO },
  );

  const [dontAssignRiskalyzePortfolio, { loading: loadingDontAssignRiskalyzePortfolio }] = useApi(
    urlWithParams('/Consumer/RiskToleranceUpdate2/PromptAssignRiskalysePortfolio', {
      clientId,
      assignRiskalysePortfolio: 'false',
    }),
    { method: 'POST', reduxActionPrefix: ASSIGN_RISKALYZE_PORTFOLIO },
  );

  useEffectOnce(() => {
    fetchRiskTolerance();

    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.RiskToleranceStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  useEffect(() => {
    if (dataChanged) {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    } else {
      ADV.util.Router.unregisterNavigateAwayCallback();
    }
  }, [dataChanged]);

  const initialValues = {
    selectedModelId,
    selectedGroupId,
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        if (!dataChanged) {
          setSubmitting(false);

          if (workflowProps) {
            workflowProps.onNext();
          }

          return;
        }

        saveRiskTolerance({
          body: {
            clientId,
            selectedPortfolioId: values.selectedModelId,
            selectedGroupId: values.selectedGroupId,
          },
          onSuccess() {
            dispatch(setSelectedGroupModel(values.selectedGroupId, values.selectedModelId));

            resetForm({
              values: {
                selectedModelId: values.selectedModelId,
                selectedGroupId: values.selectedGroupId,
              },
            });

            if (workflowProps) {
              workflowProps.onNext();
            }
          },
          onFinish() {
            setSubmitting(false);
          },
        });
      }}
    >
      {({ values, setFieldValue, resetForm, isSubmitting, dirty }) => {
        toggleDataChanged(dirty);

        const TopWrapper = workflowProps ? WorkflowWrapper : React.Fragment;

        const onAssessRiskToleranceClick = () => {
          ADV.util.Router.go2('RiskToleranceWorkflow', {
            clientId,
            redirectTo: !showPortfolioSelection
              ? 'beFi'
              : !workflowProps
              ? 'profile'
              : workflowProps.workflowId === WorkflowIdEnum.AssetBucketing
              ? 'assetBucketing'
              : 'comprehensive',
          });
        };

        return (
          <Form>
            <Container id="Profile_RiskTolerance" className="Profile">
              <TopWrapper>
                <ProfilePageHeader
                  title={isHiddenLeversRTQ ? 'Risk Profile' : 'Risk Tolerance'}
                  description={isAdviserArea ? ADVISER_AREA_SUBTITLE : CONSUMER_AREA_SUBTITLE}
                />

                {loadingRiskTolerance ? (
                  <LoadingView
                    hasOrionRiskAlyseIntegration={currentClient.HasOrionRiskAlyseIntegration}
                  />
                ) : (
                  <>
                    {riskalyseProfile && (
                      <RiskProfileSummary {...riskalyseProfile} style={{ marginBottom: 48 }} />
                    )}

                    {(currentClient.HasOrionRiskAlyseIntegration
                      ? !riskalyseProfile?.recommendedPortfolio
                      : showRiskToleranceWorkflow) && (
                      <Box mb="32">
                        {isRiskSourceFromOrion ? (
                          <OrionResult onRetakeAssessment={onAssessRiskToleranceClick} />
                        ) : (
                          <HiddenLeversRiskScore onRetakeAssessment={onAssessRiskToleranceClick} />
                        )}
                      </Box>
                    )}

                    {showPortfolioSelection && (
                      <>
                        <Card p="32">
                          <PortfolioPicker
                            groups={groupOptions}
                            modelId={values.selectedModelId}
                            groupId={values.selectedGroupId}
                            onGroupChange={groupId => {
                              setFieldValue('selectedGroupId', groupId);

                              const group = groupsById[groupId];

                              setFieldValue(
                                'selectedModelId',
                                group ? group.defaultPortfolioId : null,
                              );
                            }}
                            onModelChange={modelId => setFieldValue('selectedModelId', modelId)}
                            isHiddenLeversRTQ={isHiddenLeversRTQ}
                            showRiskToleranceWorkflow={showRiskToleranceWorkflow}
                            showPortfolioProjectedReturns={showPortfolioProjectedReturns}
                          />
                          {!!disclosure && <Disclosure>{disclosure}</Disclosure>}
                        </Card>

                        {!workflowProps && (
                          <Footer>
                            <SecondaryButton onClick={() => resetForm({ values: initialValues })}>
                              Reset
                            </SecondaryButton>
                            <PrimaryButton
                              type="submit"
                              isLoading={isSubmitting}
                              disabled={isSubmitting}
                            >
                              Save Changes
                            </PrimaryButton>
                          </Footer>
                        )}
                      </>
                    )}
                  </>
                )}
              </TopWrapper>
            </Container>

            {workflowProps && (
              <WorkflowFooter
                isSubmit
                isFirstStep={workflowProps.isFirstStep}
                isOnNextLoading={isSubmitting}
                isOnNextDisabled={values.selectedModelId === null}
                onPrevious={workflowProps.onPrevious}
              />
            )}

            {showPortfolioSelection && (
              <ConfirmationModal
                isOpen={showRiskalysePopup}
                title="We noticed a model assigned to the above risk profile."
                message="Would you like to assign this same model to the client for financial planning outcome purposes?"
                actions={
                  <>
                    <SecondaryButton
                      onClick={dontAssignRiskalyzePortfolio}
                      disabled={
                        loadingDontAssignRiskalyzePortfolio || loadingAssignRiskalyzePortfolio
                      }
                      isLoading={loadingDontAssignRiskalyzePortfolio}
                    >
                      No
                    </SecondaryButton>
                    <PrimaryButton
                      onClick={assignRiskalyzePortfolio}
                      disabled={
                        loadingAssignRiskalyzePortfolio || loadingDontAssignRiskalyzePortfolio
                      }
                      isLoading={loadingAssignRiskalyzePortfolio}
                    >
                      Yes
                    </PrimaryButton>
                  </>
                }
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default RiskTolerance;
